import { Injectable } from '@angular/core';

/**
 * Plays short audio cues using the Web Audio API — no asset files required.
 * The AudioContext is created lazily on first use and reused afterwards.
 * Browsers require a user gesture before any audio can play; since operators
 * always interact before sounds fire, this works without special unlocking.
 */
@Injectable({ providedIn: 'root' })
export class NotificationSoundService {
  private ctx: AudioContext | null = null;

  /** Single soft ping — new message from a non-active conversation. */
  playNewMessage(): void {
    this.playTone(880, 0.09);
  }

  /** Two-note ascending chime — new handoff arrived. */
  playNewHandoff(): void {
    this.playTone(660, 0.12);
    setTimeout(() => this.playTone(880, 0.18), 130);
  }

  private getCtx(): AudioContext | null {
    if (!this.ctx) {
      try {
        this.ctx = new AudioContext();
      } catch {
        return null;
      }
    }
    // Resume in case the context was suspended (browser autoplay policy)
    if (this.ctx.state === 'suspended') {
      void this.ctx.resume();
    }
    return this.ctx;
  }

  private playTone(frequency: number, duration: number): void {
    const ctx = this.getCtx();
    if (!ctx) return;

    const osc = ctx.createOscillator();
    const gain = ctx.createGain();

    osc.type = 'sine';
    osc.frequency.value = frequency;

    gain.gain.setValueAtTime(0.28, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + duration);

    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + duration + 0.01);
  }
}